import React from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { ArrowDown, Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PullToRefreshState } from '@/hooks/usePullToRefresh'

interface PullToRefreshIndicatorProps {
  state: PullToRefreshState
  pullDistance: number
  threshold?: number
}

const INDICATOR_HEIGHT = 48

const PullToRefreshIndicator: React.FC<PullToRefreshIndicatorProps> = ({
  state,
  pullDistance,
  threshold = 72,
}) => {
  const isVisible = state !== 'idle'

  // While pulling, height follows the damped distance (capped at indicator height).
  // During refresh/completing, hold at full indicator height.
  const height =
    state === 'refreshing' || state === 'completing'
      ? INDICATOR_HEIGHT
      : Math.min(pullDistance * 0.5, INDICATOR_HEIGHT)

  // Arrow rotation: 0° while pulling, 180° when triggered (ready to release)
  const arrowRotation = state === 'triggered' ? 180 : 0

  // Pull progress for opacity (0 → 1 as user approaches threshold)
  const progress = Math.min(pullDistance / threshold, 1)

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height, opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="flex items-center justify-center overflow-hidden"
        >
          {state === 'completing' ? (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 400, damping: 15 }}
            >
              <Check className="w-5 h-5 text-primary" />
            </motion.div>
          ) : state === 'refreshing' ? (
            <Loader2 className="w-5 h-5 text-primary animate-spin" />
          ) : (
            <motion.div
              animate={{ rotate: arrowRotation }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <ArrowDown
                className={cn(
                  'w-5 h-5 transition-colors',
                  state === 'triggered' ? 'text-primary' : 'text-muted-foreground',
                )}
                style={{ opacity: progress }}
              />
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default PullToRefreshIndicator
